import React from 'react';
import { HoverCard, HoverCardContent, HoverCardTrigger } from './ui/hover-card';
import { ConsolidatedPaidBadge } from './ConsolidatedPaidBadge';

const fmtDate = (d) => (d ? new Date(d).toLocaleDateString('he-IL') : '-');

const CallDetailsHover = ({ calls, money, callAmount, paidRatios = {}, testId, children }) => (
  <HoverCard openDelay={150} closeDelay={100}>
    <HoverCardTrigger asChild>
      <button type="button" className="font-black text-blue-700 underline decoration-dotted underline-offset-4 hover:text-blue-900" data-testid={testId}>
        {children}
      </button>
    </HoverCardTrigger>
    <HoverCardContent align="center" className="w-[440px] max-h-[420px] overflow-y-auto p-0" dir="rtl" data-testid={`${testId}-popup`}>
      <div className="sticky top-0 bg-indigo-600 text-white px-4 py-2 font-bold text-sm">פירוט {calls.length} קריאות</div>
      <div className="divide-y">
        {calls.map((call) => (
          <div key={call.id} className="px-4 py-3 text-sm">
            <div className="flex justify-between items-start gap-2">
              <div>
                <p className="font-bold text-indigo-700">{call.callNumber != null ? `קריאה #${call.callNumber}` : 'קריאה'} · {fmtDate(call.callDate || call.date)}</p>
                <p className="text-gray-700">סניף: {call.branch || '-'}{call.customerName ? ` · ${call.customerName}` : ''}</p>
                <p className="text-gray-700">עובד: {call.employeeName || '-'}</p>
              </div>
              <p className="font-black text-blue-800 whitespace-nowrap">₪{money(callAmount(call))}</p>
            </div>
            {(call.workItems || []).length > 0 ? (
              <ul className="mt-1 space-y-0.5 text-gray-600">
                {call.workItems.map((it, i) => (
                  <li key={i} className="flex justify-between gap-2">
                    <span className="truncate">{it.partName || '-'} × {it.quantity || 0}</span>
                    <span>₪{money((parseFloat(it.quantity) || 0) * (parseFloat(it.transactionAmount) || 0))}</span>
                  </li>
                ))}
              </ul>
            ) : call.issueDescription ? <p className="mt-1 text-gray-500">{call.issueDescription}</p> : null}
            <ConsolidatedPaidBadge compact invoiceId={call.consolidatedInvoiceId} ratio={paidRatios[call.consolidatedInvoiceId]} />
          </div>
        ))}
      </div>
    </HoverCardContent>
  </HoverCard>
);

const Th = ({ children }) => <th className="border border-gray-300 p-3 text-center">{children}</th>;
const Td = ({ children, className = '', ...rest }) => <td className={`border border-gray-300 p-3 text-center ${className}`} {...rest}>{children}</td>;

const TotalsRows = ({ totals, money, span }) => (
  <>
    <tr className="bg-blue-100 font-bold"><Td colSpan={span}>סה"כ כולל</Td><Td className="text-blue-900" data-testid="grouped-total-amount">₪{money(totals.total)}</Td></tr>
    <tr className="bg-green-50 font-bold"><Td colSpan={span} className="text-green-800">שולם בחשבון מרוכז</Td><Td className="text-green-700" data-testid="grouped-total-paid">₪{money(totals.paid)}</Td></tr>
    <tr className={`${totals.debt < 0.01 ? 'bg-green-100' : 'bg-red-50'} font-bold`}><Td colSpan={span}>יתרת חוב</Td><Td className={totals.debt < 0.01 ? 'text-green-700' : 'text-red-600'} data-testid="grouped-total-debt">₪{money(totals.debt)}</Td></tr>
  </>
);

export const groupCallsByDate = (calls) => {
  const map = {};
  calls.forEach((c) => {
    const key = String(c.callDate || c.date || '').slice(0, 10);
    (map[key] = map[key] || []).push(c);
  });
  return Object.entries(map).sort((a, b) => a[0].localeCompare(b[0]));
};

export const DateGroupedTable = ({ calls, money, callAmount, totals, paidRatios }) => (
  <table className="w-full border-collapse bg-white" data-testid="report-by-date-table">
    <thead><tr className="bg-gradient-to-r from-blue-500 to-indigo-500 text-white"><Th>תאריך</Th><Th>מס' קריאות</Th><Th>סניפים</Th><Th>סכום כולל</Th></tr></thead>
    <tbody>
      {groupCallsByDate(calls).map(([date, dayCalls]) => (
        <tr key={date} className="hover:bg-blue-50" data-testid={`report-date-row-${date}`}>
          <Td className="font-bold">{fmtDate(date)}</Td>
          <Td>{dayCalls.length}</Td>
          <Td className="text-gray-700">{[...new Set(dayCalls.map((c) => c.branch).filter(Boolean))].join(', ')}</Td>
          <Td><CallDetailsHover calls={dayCalls} money={money} callAmount={callAmount} paidRatios={paidRatios} testId={`report-date-amount-${date}`}>₪{money(dayCalls.reduce((s, c) => s + callAmount(c), 0))}</CallDetailsHover></Td>
        </tr>
      ))}
      <TotalsRows totals={totals} money={money} span={3} />
    </tbody>
  </table>
);

export const CallGroupedTable = ({ calls, money, callAmount, totals, paidRatios }) => (
  <table className="w-full border-collapse bg-white" data-testid="report-by-call-table">
    <thead><tr className="bg-gradient-to-r from-blue-500 to-indigo-500 text-white"><Th>קריאה #</Th><Th>תאריך</Th><Th>שם סניף</Th><Th>איש קשר</Th><Th>עובד</Th><Th>סכום כולל</Th></tr></thead>
    <tbody>
      {[...calls].sort((a, b) => String(a.callDate || a.date).localeCompare(String(b.callDate || b.date))).map((call) => (
        <tr key={call.id} className="hover:bg-blue-50" data-testid={`report-call-row-${call.id}`}>
          <Td>
            <p className="font-bold text-indigo-700">{call.callNumber != null ? `#${call.callNumber}` : '-'}</p>
            {call.employeeName && <p className="text-xs text-gray-600" data-testid={`report-call-employee-${call.id}`}>{call.employeeName}</p>}
          </Td>
          <Td>{fmtDate(call.callDate || call.date)}</Td>
          <Td>{call.branch}</Td>
          <Td>{call.customerName}<div><ConsolidatedPaidBadge compact invoiceId={call.consolidatedInvoiceId} ratio={paidRatios[call.consolidatedInvoiceId]} /></div></Td>
          <Td>{call.employeeName}</Td>
          <Td><CallDetailsHover calls={[call]} money={money} callAmount={callAmount} paidRatios={paidRatios} testId={`report-call-amount-${call.id}`}>₪{money(callAmount(call))}</CallDetailsHover></Td>
        </tr>
      ))}
      <TotalsRows totals={totals} money={money} span={5} />
    </tbody>
  </table>
);
